/**
 * This file defines the AdminQuoteMessagesSection component which renders nested messages.
 *
 * The messages prop should be an array of TNestedMessage objects or strings.
 *
 * A TNestedMessage object has the following structure:
 *
 * {
 *   title: string; // The title of the message
 *   value: TNestedMessage[] | string[]; // The content of the message, can be another TNestedMessage object or a string
 *   hidetitle?: boolean; // Optional, if true, the title will not be displayed
 * }
 *
 * The component recursively renders the nested messages, with different styles and components depending on the type of each message and sub-message.
 *
 * The TNestedMessage type supports nested levels up to 2 for this component. For example:
 *
 * {
 *   title: "Level 1",
 *   value: [
 *     "Level 1 Message",
 *     {
 *       title: "Level 2",
 *       value: ["Level 2 Message"]
 *     }
 *   ]
 * }
 *
 * Please note that further nesting beyond level 2 is not supported in this component and may result in unexpected behavior.
 */
import React, { FC } from "react";

import { SummaryTable } from "./SummaryTable";
import { Block } from "kanopi-components/Block";
import { ParagraphSmall } from "kanopi-components/typography";

type TAdminQuoteMessagesSectionProps = {
  messages: TNestedMessage[] | string[];
};

type TNestedMessage = {
  title: string;
  value: TNestedMessage[] | string[];
  hidetitle?: boolean;
};

type TMessageProps = {
  data: TNestedMessage[] | string[];
};
export const AdminQuoteMessagesSection: FC<TAdminQuoteMessagesSectionProps> = ({
  messages,
}) => {
  // If the messages are a simple array of strings, do not render a table
  if (Array.isArray(messages) && messages.every((item) => typeof item === "string")) {
    return <Messages data={messages} />;
  } else {
    const tableData = messages.map((item) => {
      return {
        id: item.title,
        label: item.title,
        value: <Messages data={item.value} />,
      };
    });
    return <SummaryTable data={tableData} />;
  }
};
const Messages: FC<TMessageProps> = ({ data }) => {
  return (
    <>
      {data.map((item, index) => {
        if (typeof item === "string") {
          return (
            <ParagraphSmall key={index} marginTop="scale400" marginBottom="scale400">
              {item}
            </ParagraphSmall>
          );
        } else if (typeof item === "object") {
          return (
            <>
              <Block marginBottom="scale700" marginTop="scale700">
                {!item.hidetitle && (
                  <ParagraphSmall marginTop={0} marginBottom="scale500">
                    <b>{item.title}</b>
                  </ParagraphSmall>
                )}
                <Block>
                  {item.value.map((subItem, subIndex) => {
                    if (typeof subItem === "string") {
                      return (
                        <ParagraphSmall
                          key={subIndex}
                          marginTop="scale400"
                          marginBottom="scale400"
                        >
                          {subItem}
                        </ParagraphSmall>
                      );
                    }
                    return null;
                  })}
                </Block>
              </Block>
            </>
          );
        }
        return null;
      })}
    </>
  );
};
